import type { TakiSdk } from '@taki/sdk';
import { estContenuExistant } from '@taki/sdk';
import type { ElementDeScene, SceneCroquis } from './lib/scene.js';
import { FORMAT_CROQUIS, TYPE_CROQUIS, ecrireScene } from './lib/scene.js';

/**
 * LE JEU DE DEMONSTRATION de Croquis : trois planches d'un atelier de
 * menuiserie, et elles ne se ressemblent pas.
 *
 *   « Plan de l'atelier »       -> une planche CHARGEE (zones, postes, flux en
 *                                  fleches, notes) : la vignette et le compteur
 *                                  d'elements ont quelque chose a dire ;
 *   « Parcours client »         -> un schema moyen, une file de quatre etapes ;
 *   « Idees vitrine de Noel »   -> PRESQUE VIDE, une seule note : il faut voir a
 *                                  quoi ressemble une planche qui commence.
 *
 * IDEMPOTENT PAR L'ID : chaque planche nait sous un id fixe
 * (`sdk.contents.create(..., { id })`), et le kit refuse d'ecraser un id pris
 * (`ContenuExistant`) : relancer l'import ne cree que ce qui manque, et ne
 * touche jamais une planche deja modifiee.
 *
 * CE MODULE N'IMPORTE PAS LE MOTEUR : il est dans le panneau de reglages, donc
 * dans le bundle de chaque client. La vignette, qui a besoin du moteur, se
 * fabrique par un import A LA DEMANDE, au clic.
 */

const MAINTENANT = Date.now();
let graine = 1;

function base(type: string, x: number, y: number, largeur: number, hauteur: number, extra: Record<string, unknown> = {}): ElementDeScene {
  graine += 1;
  return {
    id: `demo-${type}-${graine}`,
    type,
    x,
    y,
    width: largeur,
    height: hauteur,
    angle: 0,
    strokeColor: '#1e1e1e',
    backgroundColor: 'transparent',
    fillStyle: 'solid',
    strokeWidth: 2,
    strokeStyle: 'solid',
    roughness: 1,
    opacity: 100,
    groupIds: [],
    frameId: null,
    roundness: null,
    seed: graine * 7919,
    version: 1,
    versionNonce: graine * 104729,
    isDeleted: false,
    boundElements: null,
    updated: MAINTENANT,
    link: null,
    locked: false,
    ...extra,
  };
}

function zone(x: number, y: number, largeur: number, hauteur: number, fond: string): ElementDeScene {
  return base('rectangle', x, y, largeur, hauteur, { backgroundColor: fond, fillStyle: 'hachure', roundness: { type: 3 } });
}

function poste(x: number, y: number, largeur: number, hauteur: number): ElementDeScene {
  return base('ellipse', x, y, largeur, hauteur, { backgroundColor: '#ffec99' });
}

function texte(x: number, y: number, contenu: string, taille = 20): ElementDeScene {
  const lignes = contenu.split('\n');
  const largeur = Math.max(...lignes.map((l) => l.length)) * taille * 0.55;
  return base('text', x, y, largeur, lignes.length * taille * 1.25, {
    text: contenu,
    originalText: contenu,
    fontSize: taille,
    fontFamily: 5,
    textAlign: 'left',
    verticalAlign: 'top',
    containerId: null,
    autoResize: true,
    lineHeight: 1.25,
  });
}

function fleche(x: number, y: number, dx: number, dy: number): ElementDeScene {
  return base('arrow', x, y, Math.abs(dx), Math.abs(dy), {
    points: [
      [0, 0],
      [dx, dy],
    ],
    startArrowhead: null,
    endArrowhead: 'arrow',
    startBinding: null,
    endBinding: null,
    lastCommittedPoint: null,
    elbowed: false,
    roundness: { type: 2 },
  });
}

function planDeLAtelier(): SceneCroquis {
  return {
    fond: null,
    grille: null,
    fichiers: {},
    elements: [
      texte(0, -70, "Plan de l'atelier, rez-de-chaussée", 28),
      zone(0, 0, 320, 220, '#a5d8ff'),
      texte(16, 12, 'Réception bois'),
      poste(40, 80, 110, 70),
      texte(58, 102, 'Scie', 18),
      poste(180, 120, 110, 70),
      texte(190, 142, 'Raboteuse', 18),
      zone(380, 0, 360, 220, '#b2f2bb'),
      texte(396, 12, 'Établis'),
      poste(410, 70, 90, 60),
      texte(430, 88, 'É1', 18),
      poste(520, 70, 90, 60),
      texte(540, 88, 'É2', 18),
      poste(630, 70, 90, 60),
      texte(650, 88, 'É3', 18),
      texte(410, 160, 'Ponçage à côté de É3', 16),
      zone(0, 280, 320, 200, '#ffc9c9'),
      texte(16, 292, 'Cabine peinture'),
      texte(16, 330, 'Porte fermée pendant\nle séchage (2 h)', 16),
      zone(380, 280, 360, 200, '#eebefa'),
      texte(396, 292, 'Expédition'),
      texte(396, 330, 'Quai : camion le mardi\net le vendredi', 16),
      fleche(320, 110, 60, 0),
      fleche(560, 220, 0, 60),
      fleche(380, 380, -60, 0),
      fleche(160, 480, 380, 0),
      texte(240, 500, 'Sortie vers le quai', 16),
    ],
  };
}

function parcoursClient(): SceneCroquis {
  return {
    fond: null,
    grille: 20,
    fichiers: {},
    elements: [
      texte(0, -60, 'Parcours client en boutique', 28),
      base('rectangle', 0, 0, 180, 80, { roundness: { type: 3 } }),
      texte(20, 26, 'Accueil'),
      base('rectangle', 260, 0, 180, 80, { roundness: { type: 3 } }),
      texte(280, 26, 'Échantillons'),
      base('rectangle', 520, 0, 180, 80, { roundness: { type: 3 } }),
      texte(540, 26, 'Devis'),
      base('diamond', 780, -10, 160, 100, { backgroundColor: '#ffec99' }),
      texte(820, 26, 'Signé ?'),
      fleche(180, 40, 80, 0),
      fleche(440, 40, 80, 0),
      fleche(700, 40, 80, 0),
    ],
  };
}

function ideesVitrine(): SceneCroquis {
  return {
    fond: null,
    grille: null,
    fichiers: {},
    elements: [texte(0, 0, 'Vitrine de Noël : sapin en chutes de chêne ?', 24)],
  };
}

const PLANCHES: ReadonlyArray<{ id: string; nom: string; scene: () => SceneCroquis }> = [
  { id: 'demo-croquis-plan-atelier', nom: "Plan de l'atelier", scene: planDeLAtelier },
  { id: 'demo-croquis-parcours-client', nom: 'Parcours client', scene: parcoursClient },
  { id: 'demo-croquis-vitrine-noel', nom: 'Idées vitrine de Noël', scene: ideesVitrine },
];

export const DEMO_TOTAL = PLANCHES.length;

export async function importDemoDataset(sdk: TakiSdk, orgId: string): Promise<{ message: string }> {
  /* LA VIGNETTE A BESOIN DU MOTEUR : import a la demande, au clic seulement. */
  const { fabriquerVignette } = await import('./lib/apercu.js');
  let crees = 0;
  for (const planche of PLANCHES) {
    const scene = planche.scene();
    const preview = await fabriquerVignette(scene);
    try {
      await sdk.contents.create(
        { orgId, type: TYPE_CROQUIS, name: planche.nom, formatVersion: FORMAT_CROQUIS.version, preview },
        { id: planche.id, texte: ecrireScene(scene) },
      );
      crees += 1;
    } catch (cause) {
      if (!estContenuExistant(cause)) throw cause;
    }
  }
  return {
    message:
      crees === 0
        ? 'Rien à importer : les trois croquis de démonstration existent déjà.'
        : `${crees} croquis importé${crees > 1 ? 's' : ''}, ${DEMO_TOTAL - crees} déjà présent${DEMO_TOTAL - crees > 1 ? 's' : ''}.`,
  };
}
